import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { getAiDiscoverabilityReports, } from "@piiqtechnologies/ui-lib";
const toCommunityReport = (row) => ({
    reportId: row.reportId,
    // The endpoint calls it createdOn, but it is the completion date, and empty while a report is
    // still running.
    completedAt: row.createdOn || null,
    status: row.status,
    tier: row.tier,
    templateId: row.templateId,
    isActive: row.templateStatus === "active",
});
const EMPTY = { loading: false, reports: [] };
const NOT_LOADED = { loading: true, reports: [] };
// Long enough that moving around an org costs nothing, short enough that a report finishing
// while the user is elsewhere shows up without a reload.
const REPORTS_TTL_MS = 5 * 60 * 1000;
const COMMUNITY_PAGE = /^(?:\/o\/[^/]+)?\/community\/([0-9a-fA-F]{24})/;
// Addressed by a report or a template id, so they belong to whichever community the user
// reached them from — being on one is not having left it.
const COMMUNITY_OWNED_PAGES = /^(?:\/o\/[^/]+)?\/(?:overview|results|report)\//;
const SubjectReportsContext = createContext(null);
export const useSubjectReportsContext = () => {
    const context = useContext(SubjectReportsContext);
    if (!context) {
        throw new Error("useSubjectReportsContext must be used inside SubjectReportsProvider");
    }
    return context;
};
/**
 * The report list behind the header date dropdown: fetched lazily the first time a community
 * needs it and cached per community.
 *
 * It lives in the shell rather than in a page so the cache — and with it the selected report —
 * survives moving between a community's Report and Topics & Sources.
 */
export const SubjectReportsProvider = ({ children }) => {
    const { pathname } = useLocation();
    const [byCommunity, setByCommunity] = useState({});
    // The Community page's URL carries only the community, so its report selection is held here.
    // Living in the shell is what makes it survive the trip to Report and back.
    const [selectedByCommunity, setSelectedByCommunity] = useState({});
    // Refs, not state: reading these here would change loadReports' identity on every resolution
    // and re-trigger the effects that call it.
    const inFlightRef = useRef(new Set());
    const fetchedAtRef = useRef(new Map());
    const loadReports = useCallback((communityId) => {
        if (inFlightRef.current.has(communityId))
            return;
        const fetchedAt = fetchedAtRef.current.get(communityId);
        if (fetchedAt !== undefined && Date.now() - fetchedAt < REPORTS_TTL_MS)
            return;
        inFlightRef.current.add(communityId);
        // A refresh keeps the list it already has on screen: only the first load has nothing to
        // show, and that is the one the header skeletons for.
        setByCommunity((prev) => {
            var _a;
            const existing = prev[communityId];
            return Object.assign(Object.assign({}, prev), { [communityId]: { loading: !(existing === null || existing === void 0 ? void 0 : existing.reports.length), reports: (_a = existing === null || existing === void 0 ? void 0 : existing.reports) !== null && _a !== void 0 ? _a : [] } });
        });
        getAiDiscoverabilityReports({ subjectIds: [communityId] })
            .then((axiosResponse) => {
            var _a, _b, _c;
            const response = axiosResponse;
            inFlightRef.current.delete(communityId);
            fetchedAtRef.current.set(communityId, Date.now());
            // One group per subject, and we asked for one subject. Newest first is what the
            // selector shows; the endpoint returns them oldest first.
            const rows = (_c = (_b = (_a = response === null || response === void 0 ? void 0 : response.data) === null || _a === void 0 ? void 0 : _a[0]) === null || _b === void 0 ? void 0 : _b.reports) !== null && _c !== void 0 ? _c : [];
            const reports = [...rows].reverse().map(toCommunityReport);
            setByCommunity((prev) => (Object.assign(Object.assign({}, prev), { [communityId]: { loading: false, reports } })));
        })
            .catch((error) => {
            // No timestamp on a failure, so the next page that needs the list retries instead of
            // waiting out a TTL on data that never arrived.
            inFlightRef.current.delete(communityId);
            setByCommunity((prev) => (Object.assign(Object.assign({}, prev), { [communityId]: { loading: false, reports: [], error } })));
        });
    }, []);
    const getReports = useCallback((communityId) => { var _a; return (communityId ? ((_a = byCommunity[communityId]) !== null && _a !== void 0 ? _a : NOT_LOADED) : EMPTY); }, [byCommunity]);
    const getSelectedReportId = useCallback((communityId) => (communityId ? selectedByCommunity[communityId] : undefined), [selectedByCommunity]);
    const setSelectedReportId = useCallback((communityId, reportId) => {
        setSelectedByCommunity((prev) => (Object.assign(Object.assign({}, prev), { [communityId]: reportId })));
    }, []);
    // Leaving a community — clicking another one, or going Home — ends its selection, so coming
    // back starts from its most recent report again. The list itself is not dropped: it is reused
    // until the TTL says otherwise.
    useEffect(() => {
        var _a;
        const communityId = (_a = pathname.match(COMMUNITY_PAGE)) === null || _a === void 0 ? void 0 : _a[1];
        if (!communityId && COMMUNITY_OWNED_PAGES.test(pathname))
            return;
        setSelectedByCommunity((prev) => {
            const kept = communityId && prev[communityId] ? { [communityId]: prev[communityId] } : {};
            return Object.keys(prev).length === Object.keys(kept).length ? prev : kept;
        });
    }, [pathname]);
    const value = useMemo(() => ({ getReports, loadReports, getSelectedReportId, setSelectedReportId }), [getReports, loadReports, getSelectedReportId, setSelectedReportId]);
    return React.createElement(SubjectReportsContext.Provider, { value: value }, children);
};
